import React from "react";
import ReactDOM from "react-dom";
import { createGlobalStyle } from "styled-components";
import { OktaAuth } from "@okta/okta-auth-js";
import { Security } from "@okta/okta-react";
import { Provider } from "react-redux";
import { nest } from "recompose";
import { colors, typeface } from "@sonder/ui/source/styled/theme";
import { StoreProvider as AuthStoreProvider } from "auth/store";
import {
  isOktaEnabled,
  oktaAuthConfig,
  restoreOriginalUri,
} from "auth/utils/oktaAuth";
import { StoreProvider as CheckoutsStoreProvider } from "checkouts/store";
import { ApolloProvider } from "core/components/ApolloProvider";
import { Entry } from "core/components/Entry";
import { SentryProvider } from "core/components/SentryProvider";
import { store as ReduxStore } from "core/store";
import { StoreProvider as HistoryStoreProvider } from "history/store";
import { StoreProvider as ScheduleStoreProvider } from "schedule/store";
import { StoreProvider as SearchStoreProvider } from "search/store";
import { StoreProvider as TasksStoreProvider } from "tasks/store";
import { StoreProvider as TeamStoreProvider } from "team/store";

const AppGlobalStyles = createGlobalStyle`
  html,
  body,
  #root {
    background: ${colors.grayF};
    color: ${colors.gray4};
    display: flex;
    font-family: ${typeface.primary};
    font-size: 14px;
    height: 100%;
    line-height: 1.3;
    margin: 0;
    padding: 0;
    width: 100%;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  input,
  button {
    background: inherit;
    border: none;
    box-sizing: border-box;
    outline: none;
  }
`;

const AppProviders = nest(
  Provider,
  SentryProvider,
  ApolloProvider,
  AuthStoreProvider,
  ScheduleStoreProvider,
  CheckoutsStoreProvider,
  HistoryStoreProvider,
  TasksStoreProvider,
  TeamStoreProvider,
  SearchStoreProvider,
);

const oktaAuth = new OktaAuth(oktaAuthConfig);

function render(ApplicationEntry: React.FunctionComponent) {
  const App = () => (
    <AppProviders store={ReduxStore}>
      <ApplicationEntry />
    </AppProviders>
  );
  ReactDOM.render(
    <>
      <AppGlobalStyles />
      {isOktaEnabled ? (
        <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
          <App />
        </Security>
      ) : (
        <App />
      )}
    </>,
    document.getElementById("root"),
  );
}
render(Entry);

if (module.hot) {
  // Hot-reload everything down-tree of the store providers, but force full
  // reload if and middleware or reducers are altered.
  module.hot.accept("./core/components/Entry", () => {
    const { Entry: NextEntry } = require("./core/components/Entry"); // eslint-disable-line
    render(NextEntry);
  });
}
